/**
 * Render cropped take-off markup snippets from plan PDF pages (pdf.js + canvas overlay).
 */

import { pdfjs } from 'react-pdf';
import type { PdfMeasurement, PdfMeasurementCalibration, PdfTakeoffLink, PdfTakeoffPin } from '../types';
import { portalHostedAssetHref } from './portalAssetUrl';
import {
  canvasToJpegBytes,
  cropCanvasToNormBBox,
  drawTakeoffMarkupOnCanvas,
  estimateMarkupNormBBox,
  type NormBBox,
  type NormPoint,
} from './takeoffMarkupCanvas';

/** Minimum pixel width for cropped snippets — drives dynamic render scale. */
const MIN_CROP_PX = 2600;
const MIN_RENDER_SCALE = 3;
const MAX_RENDER_SCALE = 6;

let workerConfigured = false;

function ensurePdfWorker(): void {
  if (workerConfigured) return;
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
  workerConfigured = true;
}

const pdfCache = new Map<string, ReturnType<typeof pdfjs.getDocument>['promise']>();

async function loadPdfDocument(documentUrl: string) {
  ensurePdfWorker();
  const fetchUrl = portalHostedAssetHref(documentUrl) || documentUrl;
  let pending = pdfCache.get(fetchUrl);
  if (!pending) {
    pending = pdfjs.getDocument(fetchUrl).promise;
    pdfCache.set(fetchUrl, pending);
  }
  return pending;
}

export function clearTakeoffSnippetPdfCache(): void {
  pdfCache.clear();
}

function computeRenderScale(pageWidthPt: number, bbox: NormBBox): number {
  const bboxFraction = Math.max(0.08, (bbox.maxX - bbox.minX) / 100);
  const cropWidthPt = pageWidthPt * bboxFraction;
  const scale = MIN_CROP_PX / cropWidthPt;
  return Math.min(MAX_RENDER_SCALE, Math.max(MIN_RENDER_SCALE, scale));
}

export interface StructurePageSnippetRequest {
  documentId: string;
  documentUrl: string;
  pageNumber: number;
  structureId: string;
  structureName: string;
  links: PdfTakeoffLink[];
  measurements: PdfMeasurement[];
  calibration: PdfMeasurementCalibration | null;
  pin?: PdfTakeoffPin | null;
}

export interface StructurePageSnippet {
  documentId: string;
  documentUrl: string;
  pageNumber: number;
  structureId: string;
  structureName: string;
  imageBytes: Uint8Array;
  widthPx: number;
  heightPx: number;
  bbox: NormBBox;
}

function calibrationForPage(
  calibrations: PdfMeasurementCalibration[],
  pageNumber: number,
): PdfMeasurementCalibration | null {
  const matches = calibrations.filter(c => c.pageNumber === pageNumber);
  if (matches.length === 0) return null;
  matches.sort(
    (a, b) =>
      (b.updatedAt?.getTime() ?? b.createdAt.getTime()) -
      (a.updatedAt?.getTime() ?? a.createdAt.getTime()),
  );
  return matches[0];
}

function findStructurePin(
  pins: PdfTakeoffPin[],
  documentId: string,
  pageNumber: number,
  structureId: string,
): PdfTakeoffPin | null {
  return (
    pins.find(
      p =>
        p.structureId === structureId &&
        p.pageNumber === pageNumber &&
        (p.documentId === documentId || !p.documentId),
    ) ?? null
  );
}

/** Render one cropped snippet for a structure on a specific plan page. */
export async function renderStructurePageSnippet(
  req: StructurePageSnippetRequest,
  pins: PdfTakeoffPin[] = [],
): Promise<StructurePageSnippet | null> {
  const pageLinks = req.links.filter(
    l => l.pageNumber === req.pageNumber && l.structureId === req.structureId,
  );
  if (pageLinks.length === 0) return null;

  const linkMeasurementIds = new Set(pageLinks.map(l => l.measurementId));
  const pageMeasurements = req.measurements.filter(
    m => m.pageNumber === req.pageNumber && m.id && linkMeasurementIds.has(m.id),
  );
  if (pageMeasurements.length === 0) return null;

  const linksByMeasurementId = new Map<string, PdfTakeoffLink>();
  for (const link of pageLinks) {
    linksByMeasurementId.set(link.measurementId, link);
  }

  const pin = req.pin ?? findStructurePin(pins, req.documentId, req.pageNumber, req.structureId);
  const structurePin: NormPoint | null = pin ? { x: pin.x, y: pin.y } : null;

  const preBbox = estimateMarkupNormBBox(pageMeasurements, linksByMeasurementId, structurePin);

  const pdf = await loadPdfDocument(req.documentUrl);
  const page = await pdf.getPage(req.pageNumber);
  const baseViewport = page.getViewport({ scale: 1 });
  const renderScale = computeRenderScale(baseViewport.width, preBbox);
  const viewport = page.getViewport({ scale: renderScale });

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  await page.render({ canvasContext: ctx, viewport, canvas }).promise;

  const markupBbox = drawTakeoffMarkupOnCanvas({
    ctx,
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
    measurements: pageMeasurements,
    linksByMeasurementId,
    structurePin,
    structureName: req.structureName,
    labelScaleBBox: preBbox,
  });

  const bbox = markupBbox ?? preBbox;
  const cropped = cropCanvasToNormBBox(canvas, bbox);
  const { bytes, widthPx, heightPx } = await canvasToJpegBytes(cropped);

  return {
    documentId: req.documentId,
    documentUrl: req.documentUrl,
    pageNumber: req.pageNumber,
    structureId: req.structureId,
    structureName: req.structureName,
    imageBytes: bytes,
    widthPx,
    heightPx,
    bbox,
  };
}

export interface SnippetRenderContext {
  measurementsByDocument: Map<string, PdfMeasurement[]>;
  calibrationsByDocument: Map<string, PdfMeasurementCalibration[]>;
  pins: PdfTakeoffPin[];
}

/** Build all snippets needed for a structure across plan files/pages. */
export async function renderStructureSnippets(
  structureId: string,
  structureName: string,
  links: PdfTakeoffLink[],
  ctx: SnippetRenderContext,
  onProgress?: (current: number, total: number) => void,
): Promise<StructurePageSnippet[]> {
  const structureLinks = links.filter(l => l.structureId === structureId);
  const pageKeys = new Map<string, { documentId: string; documentUrl: string; pageNumber: number }>();
  for (const link of structureLinks) {
    const key = `${link.documentId}:${link.pageNumber}`;
    if (!pageKeys.has(key)) {
      pageKeys.set(key, {
        documentId: link.documentId,
        documentUrl: link.documentUrl,
        pageNumber: link.pageNumber,
      });
    }
  }

  const entries = Array.from(pageKeys.values());
  const snippets: StructurePageSnippet[] = [];

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    onProgress?.(i + 1, entries.length);
    const measurements = ctx.measurementsByDocument.get(entry.documentId) ?? [];
    const calibrations = ctx.calibrationsByDocument.get(entry.documentId) ?? [];
    const calibration = calibrationForPage(calibrations, entry.pageNumber);

    const snippet = await renderStructurePageSnippet(
      {
        documentId: entry.documentId,
        documentUrl: entry.documentUrl,
        pageNumber: entry.pageNumber,
        structureId,
        structureName,
        links: structureLinks,
        measurements,
        calibration,
      },
      ctx.pins,
    );
    if (snippet) snippets.push(snippet);
  }

  return snippets;
}
